import styled, { keyframes, css } from "styled-components";
import { styled as muiStyled } from '@mui/material/styles';
import { useState, useRef, useEffect, useCallback } from "react";
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate';
import EditIcon from '@mui/icons-material/Edit';
import { useCalendarState } from 'hooks/useCalendarState';
import { useUserState } from 'hooks/useUserState';
import ImageIcon from '@mui/icons-material/Image';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Button from '@mui/material/Button';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VideocamIcon from '@mui/icons-material/Videocam';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import RepeatIcon from '@mui/icons-material/Repeat';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Badge from '@mui/material/Badge';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import Tooltip from '@mui/material/Tooltip';
import { useEventHandlers } from './useEventHandlers';
import {
  EditableTitle,
  EditableDescription,
  EditableImage,
  ImageInput,
  ImageOverlay,
  EditingContainer,
  SaveButton,
  StyledDropdownButton,
  StyledMenu,
  StyledMenuItem,
  CancelButton,
  EditingSection,
  ResourceItem,
  AddButton,
  RemoveButton,
  HostsSelect,
  ActionButton,
  RecurrenceOptions,
  DaySelect,
  DayButton,
  ZoomInput,
  ButtonGroup,
  TabContainer,
  Tab,
  StyledFormControlLabel,
  SavingText,
  SavedText
} from './event-editor';
import CalendarIcon from '@mui/icons-material/CalendarToday';

const EditableDateTime = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
  
  input {
    padding: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-size: 12px;
    color: #333;
    background: white;
    
    &:focus {
      outline: none;
      border-color: #999;
    }
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    color: #B2BEB5;
  }
  16.67% {
    color: #1CCCA4;
  }
  33.33% {
    color: #B2BEB5;
  }
  50% {
    color: #1CCCA4;
  }
  66.67% {
    color: #B2BEB5;
  }
  83.33% {
    color: #1CCCA4;
  }
  100% {
    color: #B2BEB5;
  }
`;

const Events = ({
  isHidden,
  onExpand,
  onCollapse,
  sectionTitle,
  onExpandToggle,
  eventsExpanded,
  setEventsExpanded
}) => {
  const containerRef = useRef(null);
  const { events, setEvents, rsvpState, isLoading, updateRSVP, updateEvent, organizationHosts } = useCalendarState();
  const { organizations } = useUserState();
  const descriptionRef = useRef(null);
  const contentRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const dropdownRef = useRef(null);

  // Check if user is admin of any organization
  const isAdmin = organizations?.admin?.length > 0;

  // Check if user is admin of the event's organization
  const isAdminOfOrg = (organizationId) => {
    return organizations?.admin?.some(org => org.organizationId === organizationId);
  };

  const {
    editingEvent,
    tempContent,
    editorState,
    isRecurring,
    activeTab,
    showDetails,
    expandedId,
    justSaved,
    setActiveTab,
    setEditorState,
    setEditingEvent,
    updateContent,
    handleSave,
    handleDelete,
    handleRSVP,
    handleExpandClick,
    handleEventClick,
    handleEditClick,
    handleAddEventClick,
    handleCancel,
    isSaving,
    localEvents
  } = useEventHandlers({
    onExpand,
    onCollapse,
    setEventsExpanded,
    updateRSVP,
    updateEvent,
    organizations,
    containerRef,
    eventsExpanded,
    rsvpState,
    events,
  });

  useEffect(() => {
    if (editingEvent) {
      // Trigger resize for both textareas after they're rendered with content
      if (descriptionRef.current) {
        descriptionRef.current.style.height = 'inherit';
        descriptionRef.current.style.height = `${descriptionRef.current.scrollHeight}px`;
      }
      if (contentRef.current) {
        contentRef.current.style.height = 'inherit';
        contentRef.current.style.height = `${contentRef.current.scrollHeight}px`;
      }
    }
  }, [editingEvent, tempContent.description, tempContent.body_content]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        handleDropdownClose();
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleExpandIconClick = (e) => {
    e.stopPropagation();
    console.log('Expand icon clicked:', {
      eventsExpanded,
      action: eventsExpanded ? 'collapsing' : 'expanding'
    });
    setEventsExpanded(!eventsExpanded);
  };

  const handleExpandToFullEdit = (e, event) => {
    e.stopPropagation();
    onExpand();
    // Pass the current event as editEvent prop to AddEvent
    setEditingEvent(event);
  };

  const autoResize = (e) => {
    e.target.style.height = 'inherit';
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  const handleContentChange = (field, value) => {
    setTempContent(prev => {
      if (field.startsWith('meta.')) {
        const metaField = field.split('.')[1];
        return {
          ...prev,
          meta: {
            ...prev.meta,
            [metaField]: value
          }
        };
      }
      return {
        ...prev,
        [field]: value
      };
    });
  };

  const handleHostChange = (selectedHosts) => {
    handleContentChange('hosts', selectedHosts);
  };

  const handleAddResource = () => {
    handleContentChange('meta.resources', [
      ...(tempContent.meta?.resources || []),
      { name: '', link: '' }
    ]);
  };

  const handleUpdateResource = (index, field, value) => {
    const newResources = [...(tempContent.meta?.resources || [])];
    newResources[index] = {
      ...newResources[index],
      [field]: value
    };
    handleContentChange('meta.resources', newResources);
  };

  const handleRemoveResource = (index) => {
    const newResources = [...(tempContent.meta?.resources || [])];
    newResources.splice(index, 1);
    handleContentChange('meta.resources', newResources);
  };

  const handleAddGuest = () => {
    handleContentChange('meta.guests', [
      ...(tempContent.meta?.guests || []),
      { name: '', link: '' }
    ]);
  };

  const handleUpdateGuest = (index, field, value) => {
    const newGuests = [...(tempContent.meta?.guests || [])];
    newGuests[index] = {
      ...newGuests[index],
      [field]: value
    };
    handleContentChange('meta.guests', newGuests);
  };

  const handleRemoveGuest = (index) => {
    const newGuests = [...(tempContent.meta?.guests || [])];
    newGuests.splice(index, 1);
    handleContentChange('meta.guests', newGuests);
  };

  const handleRecurrenceChange = (e) => {
    const isRecurring = e.target.checked;
    setIsRecurring(isRecurring);
    if (!isRecurring) {
      handleContentChange('recurrence_pattern', null);
    } else {
      handleContentChange('recurrence_pattern', {
        frequency: 'weekly',
        interval: 1,
        days: [],
        ends: {
          type: 'after',
          count: 4
        }
      });
    }
    handleContentChange('is_recurring', isRecurring);
  };

  const handleDropdownClick = (e) => {
    e.stopPropagation();
    if (dropdownOpen) {
      handleDropdownClose();
    } else {
      setAnchorEl(e.currentTarget);
      setDropdownOpen(true);
    }
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
    setDropdownOpen(false);
  };

  const handleDropdownItemClick = (action, event) => {
    handleDropdownClose();
    switch (action) {
      case 'save':
        handleSave(event);
        break;
      case 'delete':
        handleDelete(event);
        break;
      case 'unpublish':
        // Add unpublish handler
        break;
      default:
        break;
    }
  };

  const renderEditingFields = () => (
    <div>
      <TabContainer>
        <Tab
          $active={activeTab === 'content'}
          onClick={() => setActiveTab('content')}
        >
          <DescriptionIcon fontSize="small" />
          Content
        </Tab>
        <Tab
          $active={activeTab === 'details'}
          onClick={() => setActiveTab('details')}
        >
          <SettingsIcon fontSize="small" />
          Details
        </Tab>
      </TabContainer>

      {activeTab === 'content' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <EditableDescription
            className="EditableDescription"
            ref={descriptionRef}
            value={tempContent.description}
            onChange={(e) => {
              updateContent('description', e.target.value);
              autoResize(e);
            }}
            onInput={autoResize}
            onClick={(e) => e.stopPropagation()}
            placeholder="Short description..."
          />
          <EditableDescription
            className="EditableContent"
            ref={contentRef}
            value={tempContent.body_content || ''}
            onChange={(e) => {
              updateContent('body_content', e.target.value);
              autoResize(e);
            }}
            onInput={autoResize}
            onClick={(e) => e.stopPropagation()}
            placeholder="Event details..."
            style={{ minHeight: '100px' }}
          />
        </div>
      ) : (
        <>
          <EditingSection>
            <SectionTitle>
              <CalendarIcon fontSize="small" />
              Date & Time
            </SectionTitle>
            <EditableDateTime>
              <input
                type="date"
                value={tempContent.event_date}
                onChange={(e) => updateContent('event_date', e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              <input
                type="time"
                value={tempContent.event_time}
                onChange={(e) => updateContent('event_time', e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </EditableDateTime>
          </EditingSection>

          <EditingSection>
            <SectionTitle>
              <ImageIcon fontSize="small" />
              Event Image
            </SectionTitle>
            <ImageInput
              type="url"
              value={tempContent.image_url || ''}
              onChange={(e) => updateContent('image_url', e.target.value)}
              placeholder="Image URL..."
            />
          </EditingSection>

          <EditingSection>
            <SectionTitle>
              <PersonIcon fontSize="small" />
              Event Hosts
            </SectionTitle>
            <HostsSelect
              multiple
              size="small"
              value={tempContent.hosts || []}
              onChange={(e) => updateContent('hosts', e.target.value)}
              renderValue={(selected) => (
                <AvatarGroup
                  max={3}
                  sx={{
                    justifyContent: 'flex-start',
                    '& .MuiAvatar-root': { width: 20, height: 20 }
                  }}
                >
                  {selected.map(hostId => {
                    const host = organizationHosts[editingEvent.organization_id]?.find(h => h.id === hostId);
                    return (
                      <Avatar
                        key={hostId}
                        src={host?.avatar_url}
                        alt={host?.name}
                      />
                    );
                  })}
                </AvatarGroup>
              )}
            >
              {organizationHosts[editingEvent.organization_id]?.map(host => (
                <MenuItem key={host.id} value={host.id}>
                  <Checkbox
                    checked={tempContent.hosts?.includes(host.id)}
                    size="small"
                  />
                  <Avatar
                    src={host.avatar_url}
                    sx={{ width: 20, height: 20, marginRight: 1 }}
                  />
                  {host.name}
                </MenuItem>
              ))}
            </HostsSelect>
          </EditingSection>

          <EditingSection>
            <SectionTitle>
              <VideocamIcon fontSize="small" />
              Zoom Link
            </SectionTitle>
            <ZoomInput
              type="url"
              value={tempContent.meta?.zoom_link || ''}
              onChange={(e) => updateContent('meta.zoom_link', e.target.value)}
              placeholder="https://zoom.us/j/..."
            />
          </EditingSection>

          <EditingSection>
            <SectionTitle>
              <LinkIcon fontSize="small" />
              Resources
            </SectionTitle>
            {tempContent.meta?.resources?.map((resource, index) => (
              <ResourceItem key={index}>
                <input
                  type="text"
                  value={resource.name}
                  onChange={(e) => {
                    const newResources = [...(tempContent.meta?.resources || [])];
                    newResources[index] = {
                      ...newResources[index],
                      name: e.target.value
                    };
                    updateContent('meta.resources', newResources);
                  }}
                  placeholder="Resource name"
                />
                <input
                  type="url"
                  value={resource.link}
                  onChange={(e) => {
                    const newResources = [...(tempContent.meta?.resources || [])];
                    newResources[index] = {
                      ...newResources[index],
                      link: e.target.value
                    };
                    updateContent('meta.resources', newResources);
                  }}
                  placeholder="Resource link"
                />
                <RemoveButton onClick={() => {
                  const newResources = [...(tempContent.meta?.resources || [])];
                  newResources.splice(index, 1);
                  updateContent('meta.resources', newResources);
                }}>
                  <DeleteIcon fontSize="small" />
                </RemoveButton>
              </ResourceItem>
            ))}
            <AddButton onClick={() => updateContent('meta.resources', [
              ...(tempContent.meta?.resources || []),
              { name: '', link: '' }
            ])}>
              <AddIcon fontSize="small" />
              Add Resource
            </AddButton>
          </EditingSection>

          <EditingSection>
            <SectionTitle>
              <PersonIcon fontSize="small" />
              Guests
            </SectionTitle>
            {tempContent.meta?.guests?.map((guest, index) => (
              <ResourceItem key={index}>
                <input
                  type="text"
                  value={guest.name}
                  onChange={(e) => {
                    const newGuests = [...(tempContent.meta?.guests || [])];
                    newGuests[index] = {
                      ...newGuests[index],
                      name: e.target.value
                    };
                    updateContent('meta.guests', newGuests);
                  }}
                  placeholder="Guest name"
                />
                <input
                  type="url"
                  value={guest.link}
                  onChange={(e) => {
                    const newGuests = [...(tempContent.meta?.guests || [])];
                    newGuests[index] = {
                      ...newGuests[index],
                      link: e.target.value
                    };
                    updateContent('meta.guests', newGuests);
                  }}
                  placeholder="Guest link (optional)"
                />
                <RemoveButton onClick={() => {
                  const newGuests = [...(tempContent.meta?.guests || [])];
                  newGuests.splice(index, 1);
                  updateContent('meta.guests', newGuests);
                }}>
                  <DeleteIcon fontSize="small" />
                </RemoveButton>
              </ResourceItem>
            ))}
            <AddButton onClick={() => updateContent('meta.guests', [...(tempContent.meta?.guests || []), { name: '', link: '' }])}>
              <AddIcon fontSize="small" />
              Add Guest
            </AddButton>
          </EditingSection>

          <EditingSection>
            <SectionTitle>
              <RepeatIcon fontSize="small" />
              Recurrence
            </SectionTitle>
            <StyledFormControlLabel
              control={
                <Checkbox
                  checked={isRecurring}
                  onChange={(e) => {
                    const isRecurring = e.target.checked;
                    updateContent('is_recurring', isRecurring);
                    updateContent('recurrence_pattern', isRecurring ? {
                      frequency: 'weekly',
                      interval: 1,
                      days: [],
                      ends: { type: 'after', count: 4 }
                    } : null);
                  }}
                  size="small"
                />
              }
              label="Repeat this event"
            />
            {isRecurring && (
              <RecurrenceOptions>
                <Select
                  size="small"
                  value={tempContent.recurrence_pattern?.frequency || 'weekly'}
                  onChange={(e) => updateContent('recurrence_pattern', {
                    ...tempContent.recurrence_pattern,
                    frequency: e.target.value
                  })}
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>

                {tempContent.recurrence_pattern?.frequency === 'weekly' && (
                  <DaySelect>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
                      <DayButton
                        key={day}
                        $selected={tempContent.recurrence_pattern?.days?.includes(index)}
                        onClick={() => {
                          const days = tempContent.recurrence_pattern?.days || [];
                          const newDays = days.includes(index)
                            ? days.filter(d => d !== index)
                            : [...days, index];
                          updateContent('recurrence_pattern', {
                            ...tempContent.recurrence_pattern,
                            days: newDays
                          });
                        }}
                      >
                        {day}
                      </DayButton>
                    ))}
                  </DaySelect>
                )}

                <Select
                  size="small"
                  value={tempContent.recurrence_pattern?.ends?.type || 'after'}
                  onChange={(e) => updateContent('recurrence_pattern', {
                    ...tempContent.recurrence_pattern,
                    ends: {
                      type: e.target.value,
                      ...(e.target.value === 'after' ? { count: 4 } : {})
                    }
                  })}
                >
                  <MenuItem value="after">Repeat</MenuItem>
                  <MenuItem value="on">Until</MenuItem>
                </Select>

                {tempContent.recurrence_pattern?.ends?.type === 'after' && (
                  <input
                    type="number"
                    min="1"
                    max="52"
                    value={tempContent.recurrence_pattern?.ends?.count || 4}
                    onChange={(e) => updateContent('recurrence_pattern', {
                      ...tempContent.recurrence_pattern,
                      ends: {
                        type: 'after',
                        count: parseInt(e.target.value, 10)
                      }
                    })}
                  />
                )}

                {tempContent.recurrence_pattern?.ends?.type === 'on' && (
                  <input
                    type="date"
                    value={tempContent.recurrence_pattern?.ends?.date || ''}
                    onChange={(e) => updateContent('recurrence_pattern', {
                      ...tempContent.recurrence_pattern,
                      ends: {
                        type: 'on',
                        date: e.target.value
                      }
                    })}
                  />
                )}
              </RecurrenceOptions>
            )}
          </EditingSection>
        </>
      )}
    </div>
  );

  const renderEventDetails = (event) => (
    <BodyContent>
      <DetailSection>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {event.hostDetails?.length > 0 && (
            <div>
              <SectionTitle style={{ marginBottom: '4px' }}>
                <PersonIcon fontSize="small" />
                Hosted by
              </SectionTitle>
              <AvatarContainer>
                <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24 } }}>
                  {event.hostDetails.map(host => (
                    <Avatar
                      key={host.id}
                      src={host.avatar_url}
                      alt={host.name}
                      title={host.name}
                    />
                  ))}
                </AvatarGroup>
              </AvatarContainer>
            </div>
          )}

          {event.meta?.zoom_link && (
            <div>
              <a href={event.meta.zoom_link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px' }}>
                <VideocamIcon fontSize="small" />
                Join Zoom Meeting
              </a>
            </div>
          )}

          {event.meta?.resources?.length > 0 && (
            <div>
              <SectionTitle style={{ marginBottom: '4px' }}>
                <LinkIcon fontSize="small" />
                Resources
              </SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {event.meta.resources.map((resource, index) => (
                  <a
                    key={index}
                    href={resource.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: '13px' }}
                  >
                    <LinkIcon fontSize="small" />
                    {resource.name}
                  </a>
                ))}
              </div>
            </div>
          )}

          {event.meta?.guests?.length > 0 && (
            <div>
              <SectionTitle style={{ marginBottom: '4px' }}>
                <PersonIcon fontSize="small" />
                Special Guests
              </SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {event.meta.guests.map((guest, index) => (
                  <div key={index}>
                    {guest.link ? (
                      <a href={guest.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: '13px' }}>
                        <PersonIcon fontSize="small" />
                        {guest.name}
                      </a>
                    ) : (
                      <span style={{ fontSize: '13px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <PersonIcon fontSize="small" />
                        {guest.name}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {event.is_recurring && (
            <div>
              <SectionTitle style={{ marginBottom: '4px' }}>
                <RepeatIcon fontSize="small" />
                Recurring Event
              </SectionTitle>
              <div style={{ fontSize: '13px', color: '#666' }}>
                {event.recurrence_pattern?.frequency === 'weekly' &&
                  `Weekly on ${event.recurrence_pattern.days
                    .map(day => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day])
                    .join(', ')}`
                }
                {event.recurrence_pattern?.ends?.type === 'after' &&
                  ` for ${event.recurrence_pattern.ends.count} occurrences`
                }
                {event.recurrence_pattern?.ends?.type === 'on' &&
                  ` until ${new Date(event.recurrence_pattern.ends.date).toLocaleDateString()}`
                }
              </div>
            </div>
          )}
        </div>
      </DetailSection>

      <div className="Description">{event.description}</div>
      <div className="BodyContent">{event.body_content}</div>
    </BodyContent>
  );

  return (
    <EventsContainer
      ref={containerRef}
      $isExpanded={expandedId !== null}
    >
      {!expandedId && (
        <ContainerHeader>
          <SectionName>Calendar</SectionName>
          <ActionButtons>
            <Tooltip title={eventsExpanded ? "Collapse events section" : "Expand events section"}>
              <ExpandIcon
                fontSize="small"
                onClick={handleExpandIconClick}
                $isExpanded={eventsExpanded}
              />
            </Tooltip>
            {isAdmin && (
              <AddEventIcon fontSize="small" onClick={handleAddEventClick} />
            )}
          </ActionButtons>
        </ContainerHeader>
      )}
      {localEvents?.map((event) => (
        <EventCard
          key={event.id === 'new' ? 'new' : event.id}
          $isExpanded={expandedId === event.id}
          $hide={expandedId !== null && expandedId !== event.id}
          $isEditing={editingEvent?.id === event.id}
          onClick={() => handleEventClick(event.id)}
        >
          <div>
            <ImageContainer $isEditing={editingEvent?.id === event.id}>
              {editingEvent?.id === event.id ? (
                <>
                  <EditableImage>
                    <img src={tempContent.image_url || 'https://images.unsplash.com/photo-1494587416117-f102a2ac0a8d?q=80&w=2340&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'} alt={event.title} />
                  </EditableImage>
                </>
              ) : (
                <>
                  <img src={event.image_url || 'https://images.unsplash.com/photo-1494587416117-f102a2ac0a8d?q=80&w=2340&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'} alt={event.title} />
                  {event.id !== 'new' && (
                    <>
                      <RSVPButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRSVP(event.id);
                        }}
                        $isAttending={rsvpState[event.id]}
                        disabled={isLoading}
                      >
                        {rsvpState[event.id] ? 'Attending!' : 'RSVP?'}
                      </RSVPButton>
                      {showDetails === event.id && isAdminOfOrg(event.organization_id) && (
                        <EditButton onClick={(e) => handleEditClick(e, event)}>
                          Edit
                        </EditButton>
                      )}
                    </>
                  )}
                </>
              )}
            </ImageContainer>
            <CardContent $isEditing={editingEvent?.id === event.id}>
              <Header $isEditing={editingEvent?.id === event.id}>
                {editingEvent?.id === event.id ? (
                  <EditableTitle
                    value={tempContent.title}
                    onChange={(e) => {
                      updateContent('title', e.target.value);
                      e.target.style.height = 'inherit';
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onInput={(e) => {
                      e.target.style.height = 'inherit';
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    rows={1}
                  />
                ) : (
                  <Title>{event.title}</Title>
                )}
                {editingEvent?.id !== event.id && event.id !== 'new' && (
                  <DateTime>
                    {new Date(event.event_date + 'T12:00:00').toLocaleDateString('en-US', {
                      month: 'short',
                      day: '2-digit',
                      timeZone: 'America/New_York'
                    })}
                    &nbsp; @ {new Date(`1970-01-01T${event.event_time}`).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                      timeZone: 'America/New_York'
                    })}
                  </DateTime>
                )}
                {editingEvent?.id !== event.id && !expandedId && event.id !== 'new' && (
                  <ExpandLink>{showDetails === event.id ? 'Hide Details' : 'Show Details'}</ExpandLink>
                )}
                {editingEvent?.id !== event.id && showDetails === event.id && event.id !== 'new' && (
                  <>
                    <div className="Description">{event.description}</div>
                    <div className="BodyContent">{event.body_content}</div>
                  </>
                )}
              </Header>
            </CardContent>
          </div>
          {editingEvent?.id === event.id && (
            <EditingContainer>
              {renderEditingFields()}
              <ButtonGroup>
                <SaveButton onClick={() => handleSave(event)}>
                  {isSaving ? <SavingText>Saving...</SavingText> : justSaved ? <SavedText>Saved!</SavedText> : event.id === 'new' ? 'Create Event' : 'Save Changes'}
                </SaveButton>
                <div ref={dropdownRef}>
                  <StyledDropdownButton
                    onClick={handleDropdownClick}
                    endIcon={<ArrowDropDownIcon />}
                    size="small"
                  />
                  <StyledMenu
                    anchorEl={anchorEl}
                    open={dropdownOpen}
                    onClose={handleDropdownClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    MenuListProps={{
                      'aria-labelledby': 'actions-button',
                    }}
                  >
                    <StyledMenuItem onClick={() => handleDropdownItemClick('save', event)}>
                      <span className="menu-item-content">
                        {isSaving ? <SavingText>Saving...</SavingText> : justSaved ? <SavedText>Saved!</SavedText> : event.id === 'new' ? 'Create Event' : 'Save Changes'}
                      </span>
                    </StyledMenuItem>
                    {event.id !== 'new' && (
                      <>
                        <StyledMenuItem onClick={() => handleDropdownItemClick('unpublish', event)}>
                          <span className="menu-item-content">
                            Unpublish
                          </span>
                        </StyledMenuItem>
                        <StyledMenuItem
                          onClick={() => handleDropdownItemClick('delete', event)}
                          $isDestructive
                        >
                          <span className="menu-item-content">
                            Delete Event
                          </span>
                        </StyledMenuItem>
                      </>
                    )}
                  </StyledMenu>
                </div>
                <CancelButton onClick={handleCancel}>{justSaved ? 'Close' : 'Cancel'}</CancelButton>
              </ButtonGroup>
            </EditingContainer>
          )}
        </EventCard>
      ))}
      {expandedId !== null && (
        <CollapseButton onClick={() => handleExpandClick(expandedId)}>
          <span>👈🏽 Back to Events</span>
        </CollapseButton>
      )}
    </EventsContainer>
  );
};

const fadeInSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInSlideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const EventsContainer = styled.div.attrs({
  className: 'EventsContainer'
})`
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${({ $isExpanded }) => $isExpanded ? 'transparent' : 'white'};
  box-sizing: border-box;
  border-radius: 5px;
  overflow-y: ${({ $isExpanded }) => $isExpanded ? 'hidden' : 'auto'};
  padding: ${({ $isExpanded }) => $isExpanded ? '0px' : '0px 15px 10px'};
  position: relative;
  > div:not(.ContainerHeader) {
    transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: opacity;
  }

  ${({ $isAdding }) => $isAdding && `
    > div:not(.ContainerHeader) {
      animation: fadeIn 0.8s cubic-bezier(0.4, 0, 0.2, 1);
    }

    @keyframes fadeIn {
      0% { opacity: 0; }
      100% { opacity: 1; }
    }
  `}

  &::-webkit-scrollbar-thumb {
    background: #e5e5e5;
    border-radius: 20px;
  }
`;

const SectionName = styled.div`
  font-size: 19px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 2px;
  svg {
    font-size: 14px;
  }
`;

const SectionTitle = styled(SectionName)`
  color: #666;
  font-size: 12px;
`;

const EventCard = styled.div.attrs({
  className: 'EventCard'
})`
  display: flex;
  flex-direction: column;
  background-color: ${({ $isExpanded }) => $isExpanded ? 'white' : 'none'};
  padding: ${({ $isExpanded, $isEditing }) => ($isExpanded || $isEditing) ? '0px' : '0px'};
  border-radius: 5px;
  position: relative;
  gap: 5px;
  display: ${({ $hide }) => $hide ? 'none' : 'flex'};
  min-width: 0;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  padding: 10px;
  margin: 0px -5px;
  box-sizing: border-box;
  &:hover {
    ${({ $isExpanded, $isEditing }) => !$isExpanded && !$isEditing && `
      border-radius: 5px;
      transition: all 0.2s ease;
    `}
    img {
      border-radius: 3px 0px 0px 3px;
    }
    .RSVPButton {
      letter-spacing: .5px;
      transition: all 0.2s ease;
    }
    .ExpandLink {
      color: green;
      transition: all 0.3s;
    }
  }

  .Description, .BodyContent {
    opacity: 0;
    animation: ${fadeIn} 0.3s ease forwards;
  }
  
  ${({ $isExpanded }) => $isExpanded && `
    flex: 1;
    height: 100%;
    overflow-y: auto;
    background: white;
  `}
  
  ${({ $hide }) => $hide && `
    display: none;
  `}

  cursor: default;

  > div:first-child {
    display: flex;
    flex-direction: row;
    gap: 5px;
  }
`;

const ImageContainer = styled.div.attrs({
  className: 'ImageContainer'
})`
  display: flex;
  flex-direction: column;
  gap: 5px;
  width: 98px;
  flex-shrink: 0;
  
  img {
    width: 98px;
    height: 80px;
    object-fit: cover;
    border-radius: 3px;
  }
  
  ${({ $isEditing }) => $isEditing && `
    .ButtonGroup {
      display: none;
    }
  `}
`;

const CardContent = styled.div.attrs({
  className: 'CardContent'
})`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  padding-top: 13px;
  box-sizing: border-box;
  gap: 12px;
  padding: 13px 10px 8px 10px;
  border-radius: 4px 3px 3px;
  
  ${({ $isEditing }) => $isEditing && `
    cursor: default;
    
    input[type="text"],
    input[type="url"],
    input[type="date"],
    input[type="time"],
    input[type="number"],
    textarea,
    .DraftEditor-root {
      cursor: text;
    }
    
    button,
    select,
    .MuiSelect-select {
      cursor: default;
    }
  `}
`;

const Header = styled.div.attrs({
  className: 'Header'
})`
  display: flex;
  flex: 1;
  align-items: flex-start;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  margin-left: ${({ $isEditing }) => $isEditing ? '0' : '8px'};

  .Description {
    margin-top: 4px;
    color: #333;
    font-size: 12px;
    line-height: 17px;
    font-weight: 400;
  }

  .BodyContent {
    margin-top: 4px;
    background: #e9e9e9;
    padding: 10px;
    border-radius: 3px;
    font-size: 12px;
    line-height: 17px;
    color: #333;
    font-weight: 400;
    width: 96%;
    box-sizing: border-box;
  }
`;

const Title = styled.h3.attrs({
  className: 'EventTitle'
})`
  margin: 0;
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
  width: 100%;
  font-family: Arial;
`;

const DateTime = styled.span.attrs({
  className: 'DateTime'
})`
  margin-left: -2px;
  font-size: 12px;
  background: #e9e9e9;
  color: #333;
  width: fit-content;
  padding: 5px 10px;
  border-radius: 3px;
  box-sizing: border-box;
`;

const ContentWrapper = styled.div.attrs({
  className: 'ContentWrapper'
})`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 9px;
  flex: 1;
  padding-right: 12px;
`;

const Description = styled.span.attrs({
  className: 'Description'
})`
  color: #333;
  font-size: 12px;
  line-height: 17px;
  font-weight: 400;
`;

const ActionRow = styled.div.attrs({
  className: 'ActionRow'
})`
  display: flex;
  align-items: center;
  gap: 15px;
  justify-content: space-between;
`;

const RSVPButton = styled.button.attrs({
  className: 'RSVPButton'
})`
  background: ${({ $isAttending }) => $isAttending ? '#4CAF50' : 'black'};
  color: white;
  border: none;
  padding: ${({ $isExpanded }) => $isExpanded ? '6px 12px' : '10px'}; 
  border-radius: 3px; 
  cursor: default;
  font-size: 11px;
  font-weight: 800;
  transition: all 0.2s ease;
  margin-top: -5px;
  margin: 0 auto;
  width: 100%;
  &:hover {
    background: #333;
  }
`;

const ExpandLink = styled.span.attrs({
  className: 'ExpandLink'
})`
  color: #a1a1a1;
  text-decoration: underline;
  font-size: 11px;
  margin-top: 4px;
  align-self: flex-start;
  
  &:hover {
    color: #666;
  }
`;

const BodyContent = styled.div`
  color: #333;
  font-size: 12px;
  line-height: 17px;
  padding: 8px 0;
  white-space: pre-wrap;
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: ${fadeIn} 0.2s ease-out;

  .Description {
    color: #333;
    font-size: 12px;
    line-height: 17px;
    font-weight: 400;
  }

  .BodyContent {
    background: #e9e9e9;
    padding: 10px;
    border-radius: 3px;
    font-size: 12px;
    line-height: 17px;
    color: #333;
    font-weight: 400;
  }
`;

const CollapseButton = styled.div.attrs({
  className: 'CollapseButton'
})`
  border-radius: 3px;
  font-size: 14px;
  background: black;
  color: white !important;
  box-sizing: border-box;
  cursor: default;
  opacity: 1;
  animation-delay: 0.2s;
  margin: 10px 0px 0px;
  height: initial;
  border-radius: 25px;
  padding: 3px 25px 5px 15px;
  width: fit-content;

  &:hover {
    background: #333;
  }
`;

const ContainerHeader = styled.div.attrs({
  className: 'ContainerHeader'
})`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  position: sticky;
  top: 0;
  left: 0;
  background: white;
  padding: 22px 0px 5px 0px;
  z-index: 1000;
  background: white;
  margin: 0px -10px;
  padding: 25px 10px 0px;
  &.AddEvent {
    padding: 0px 5px;
    background: transparent;
    .SectionTitle {
      background: transparent;
    }
  }
`;

const ActionButtons = styled.div.attrs({
  className: 'ActionButtons'
})`
  display: flex;
  gap: 10px;
  background: white;
  border-radius: 25px;
  padding: 0px 8px;
  justify-content: flex-end;
`;

const ExpandIcon = styled(AspectRatioIcon)`
  color: #B2BEB5;
  transition: all 0.3s;
  ${({ $isExpanded }) => $isExpanded && css`
    animation: ${pulse} 2s ease-in-out;
    animation-iteration-count: 1;
    color: #1CCCA4;
  `}
  &:hover {
    color: #000;
  }
`;

const AddEventIcon = styled(ControlPointDuplicateIcon)`
  color: #B2BEB5;
  &:hover {
    color: #000;
    transition: all 0.3s;
  }
`;

const EditButton = styled.button.attrs({
  className: 'EditButton'
})`
  background: black;
  color: white;
  border: none;
  padding: 10px;
  border-radius: 3px;
  cursor: default;
  font-size: 11px;
  font-weight: 800;
  transition: all 0.2s ease;
  margin-top: -5px;
  transition: all 0.3s ease-in-out;
  width: 100%;
  margin: 0 auto;
  &:hover {
    background: #333;
  }
`;

const DetailSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }

  a {
    color: #2563eb;
    text-decoration: none;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 4px;

    &:hover {
      text-decoration: underline;
    }

    svg {
      font-size: 14px;
    }
  }
`;

const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;

  .MuiAvatar-root {
    border: 2px solid white;
  }
`;

export default Events;